import { logger } from "../../../utils/logger";
import { ApiError } from "../types/common";

/**
 * Utilitário centralizado para tratamento de erros da API
 * 
 * Funcionalidades:
 * - Tratamento consistente de erros
 * - Logs padronizados
 * - Mensagens de erro amigáveis
 * - Retry automático para erros específicos
 */
export class ApiErrorHandler {
  /**
   * Trata erros de requisição da API
   */
  static handleRequestError(error: any, context: string): never {
    const errorInfo = {
      context,
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      message: error.message,
      data: error.response?.data,
    };

    // Log do erro
    logger.error(`[${context}] API Error:`, errorInfo);

    // Tratamento específico por tipo de erro
    if (error.response) {
      // Erro de resposta do servidor
      const apiError = error.response.data as ApiError;
      throw new Error(apiError?.detail || `Erro ${error.response.status}: ${error.response.statusText}`);
    } else if (error.request) {
      // Erro de rede
      throw new Error(`Erro de conexão: ${error.message}`);
    } else {
      // Erro de configuração
      throw new Error(`Erro de configuração: ${error.message}`);
    }
  }

  /**
   * Verifica se um erro é de constraint violation
   */
  static isConstraintViolation(error: any): boolean {
    return (
      error.response?.status === 400 &&
      (error.response.data?.detail?.includes("already exists") ||
        error.response.data?.detail?.includes("unique constraint"))
    );
  }

  /**
   * Verifica se um erro é de autenticação
   */
  static isAuthError(error: any): boolean {
    return error.response?.status === 401 || error.response?.status === 403;
  }

  /**
   * Verifica se um erro é de rede
   */
  static isNetworkError(error: any): boolean {
    return !error.response && error.request;
  }

  /**
   * Retorna mensagem de erro amigável
   */
  static getFriendlyMessage(error: any): string {
    if (this.isAuthError(error)) {
      return "Sessão expirada. Faça login novamente.";
    }
    
    if (this.isNetworkError(error)) {
      return "Erro de conexão. Verifique sua internet.";
    }

    if (this.isConstraintViolation(error)) {
      return "Item já existe no sistema.";
    }

    return error.message || "Erro desconhecido.";
  }
} 